import { useState } from "react"
import { usePage } from "@inertiajs/react"
import { Link2, Link2Off, Plus, X as CloseIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { DateTimePicker } from "@/components/ui/datepicker"
import XIcon from "@/assets/icons/x.svg?react"
import LinkedinIcon from "@/assets/icons/linkedin.svg?react"

import TwitterThreadEditor from "./twitter_thread_editor"
import LinkedinThreadEditor from "./linkedin_thread_editor"

const cx = (...classes) => classes.filter(Boolean).join(" ")

function CrossPostToggle({ enabled, onToggle }) {
  const [showLabel, setShowLabel] = useState(false)
  const [visibleActionLabel, setVisibleActionLabel] = useState("Cross-post")
  const label = enabled ? "Cross-posting enabled" : "Enable cross-posting"
  const Icon = enabled ? Link2 : Link2Off
  const actionLabel = enabled ? "Disable cross-post" : "Enable cross-post"
  const showCurrentLabel = () => {
    setVisibleActionLabel(actionLabel)
    setShowLabel(true)
  }
  const handleClick = () => {
    setShowLabel(false)
    onToggle()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={showCurrentLabel}
      onMouseLeave={() => setShowLabel(false)}
      onFocus={showCurrentLabel}
      onBlur={() => setShowLabel(false)}
      className="flex h-7 items-center rounded-md text-xs text-muted-foreground transition-colors hover:text-foreground"
      aria-label={label}
      title={label}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className={cx(
        "pr-2 font-medium transition-opacity duration-200",
        showLabel ? "opacity-100" : "opacity-0"
      )}>
        {visibleActionLabel}
      </span>
    </button>
  )
}

function ConnectAccountsPrompt() {
  const { organization } = usePage().props.auth

  const platforms = [
    { platform: "linkedin", logo: "/linkedin.png", name: "LinkedIn", description: "Publish posts to your LinkedIn profile, and add first comments." },
    { platform: "twitter", logo: "/twitter.png", name: "Twitter", description: "Cross-post your content to X." },
  ]

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground text-center">
        Connect an account to start publishing.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {platforms.map(({ platform, logo, name, description }) => (
          <div key={platform} className="flex flex-col gap-3 p-4 border rounded-md">
            <div className="flex items-center gap-1.5">
              <img src={logo} alt={name} className="size-5 shrink-0" />
              <span className="font-medium text-sm">{name}</span>
            </div>
            <p className="text-xs text-muted-foreground">{description}</p>
            <div className="mt-auto">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  const returnTo = window.location.pathname
                  window.location.href = `/auth/${organization.nano_id}/late/connect/${platform}?return_to=${encodeURIComponent(returnTo)}`
                }}
              >
                Connect
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function PostCreationBlock({
  scheduledAt,
  onScheduleChange,
  onSubmit,
  onPublishNow,
  onNewDraft,
  isSubmitDisabled,
  errors = {},
  twitterPosts,
  onUpdateTwitterPost,
  onAddTwitterPost,
  onRemoveTwitterPost,
  linkedinPosts,
  onUpdateLinkedinPost,
  onAddLinkedinPost,
  onRemoveLinkedinPost,
  crossPostEnabled,
  onCrossPostToggle,
  assets = [],
  onMediaChange,
  mediaSelection,
  organizationName,
  userEmail,
  integrations,
  onReplyChange,
  organizationId,
}) {
  const [showSchedule, setShowSchedule] = useState(false)
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const [targets, setTargets] = useState(() => ({
    twitter: !!integrations?.twitter?.connected,
    linkedin: !!integrations?.linkedin?.connected,
  }))
  const [selectionOrder, setSelectionOrder] = useState(() => {
    const order = []
    if (integrations?.twitter?.connected) order.push("twitter")
    if (integrations?.linkedin?.connected) order.push("linkedin")
    return order
  })
  const [activePreview, setActivePreview] = useState(() => {
    if (integrations?.twitter?.connected) return "twitter"
    if (integrations?.linkedin?.connected) return "linkedin"
    return null
  })
  
  const twitterProfile = integrations?.twitter
  const linkedinProfile = integrations?.linkedin
  
  // Twitter profile data
  const twitterDisplayName = twitterProfile?.display_name || twitterProfile?.handle || organizationName || "Your company"
  const twitterHandleLabel = twitterProfile?.handle || (userEmail ? userEmail.split("@")[0] : "founder")
  const twitterAvatarUrl = twitterProfile?.avatar_url
  const twitterVerified = twitterProfile?.verified || false
  
  // LinkedIn profile data
  const linkedinDisplayName = linkedinProfile?.display_name || linkedinProfile?.handle || organizationName || "Your company"
  const linkedinHandleLabel = linkedinProfile?.handle || (userEmail ? userEmail.split("@")[0] : "founder")
  const linkedinAvatarUrl = linkedinProfile?.avatar_url
  
  const twitterEnabled = !!integrations?.twitter?.connected
  const linkedinEnabled = !!integrations?.linkedin?.connected
  const platformKeys = ["twitter", "linkedin"]

  const selectedTargets = Object.entries(targets)
    .filter(([_, value]) => value)
    .map(([key]) => key)

  const hasSelectedTargets = selectedTargets.length > 0

  const selectPreview = (key, enabled) => {
    if (!enabled) return
    if (!targets[key]) {
      setTargets((prev) => ({ ...prev, [key]: true }))
      setSelectionOrder((prev) => (prev.includes(key) ? prev : [...prev, key]))
    }
    setActivePreview(key)
  }

  const toggleTarget = (key, enabled) => {
    if (!enabled) return

    const wasSelected = targets[key]
    const nextTargets = { ...targets, [key]: !wasSelected }
    const nextSelectionOrder = wasSelected
      ? selectionOrder.filter((platform) => platform !== key)
      : selectionOrder.includes(key)
        ? selectionOrder
        : [...selectionOrder, key]

    setTargets(nextTargets)
    setSelectionOrder(nextSelectionOrder)

    if (wasSelected) {
      if (activePreview === key) {
        setActivePreview(nextSelectionOrder[0] || null)
      }
      const nextHasTargets = Object.values(nextTargets).some(Boolean)
      if (!nextHasTargets) setShowSchedule(false)
      return
    }

    setActivePreview(key)
  }

  const selectedPlatformsInOrder = selectionOrder.filter((platform) => targets[platform])
  const selectedNotTracked = platformKeys.filter(
    (platform) => targets[platform] && !selectedPlatformsInOrder.includes(platform)
  )
  const unselectedPlatforms = platformKeys.filter((platform) => !targets[platform])
  const pillOrder = [...selectedPlatformsInOrder, ...selectedNotTracked, ...unselectedPlatforms]

  const assetError = errors.asset ||
    errors.asset_id ||
    errors.file ||
    errors.asset_attachable ||
    errors["asset_attributes.file"] ||
    errors["asset_attachment_attributes.asset_id"]

  return (
    <form onSubmit={(e) => {
        onSubmit(e, selectedTargets)
      }} className="space-y-6">
      <div className="mx-auto w-full max-w-[860px] p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {hasSelectedTargets && (
              <>
                <div className="flex items-center gap-1 rounded-md bg-background py-1 shadow-sm">
                  {pillOrder.map((platform) => {
                    const isTwitter = platform === "twitter"
                    const enabled = isTwitter ? twitterEnabled : linkedinEnabled
                    const selected = targets[platform]
                    const active = activePreview === platform
                    const displayName = isTwitter ? twitterDisplayName : linkedinDisplayName
                    const selectedIconSize = isTwitter ? "h-3 w-3" : "h-3.5 w-3.5"
                    const unselectedIconSize = "h-3.5 w-3.5"

                    return (
                      <div
                        key={platform}
                        className={cx(
                          "group flex items-center rounded-lg px-0.5 transition-colors duration-200",
                          selected && active
                            ? "bg-foreground text-background hover:bg-foreground/90"
                            : selected
                              ? "text-foreground hover:bg-muted/60"
                              : "bg-transparent text-muted-foreground hover:bg-muted/40",
                          !enabled && "cursor-not-allowed opacity-50"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => selectPreview(platform, enabled)}
                          disabled={!enabled}
                          className={cx(
                            "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition",
                            selected && !active && "text-foreground"
                          )}
                        >
                          {selected ? (
                            isTwitter ? (
                              <XIcon className={`${selectedIconSize} fill-current`} />
                            ) : (
                              <LinkedinIcon className={`${selectedIconSize} fill-current`} />
                            )
                          ) : (
                            <span className={`relative block ${unselectedIconSize}`}>
                              <Plus className={`${unselectedIconSize} absolute inset-0 opacity-100 transition-opacity duration-200 ease-in-out group-hover:opacity-0`} />
                              {isTwitter ? (
                                <XIcon className={`${unselectedIconSize} absolute inset-0 fill-current opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100`} />
                              ) : (
                                <LinkedinIcon className={`${unselectedIconSize} absolute inset-0 fill-current opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100`} />
                              )}
                            </span>
                          )}

                          {selected ? (
                            <span className="max-w-32 truncate text-[11px] font-medium">
                              {displayName}
                            </span>
                          ) : (
                            <span className="max-w-0 overflow-hidden whitespace-nowrap text-[11px] opacity-0 transition-all duration-200 group-hover:max-w-32 group-hover:opacity-100">
                              {displayName}
                            </span>
                          )}
                        </button>

                        {selected && (
                          <button
                            type="button"
                            onClick={() => toggleTarget(platform, enabled)}
                            disabled={!enabled}
                            className="rounded-full p-0.5 transition hover:bg-background/20"
                            aria-label={isTwitter ? "Remove X target" : "Remove LinkedIn target"}
                          >
                            <CloseIcon className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>

                {targets.twitter && targets.linkedin && (
                  <CrossPostToggle enabled={crossPostEnabled} onToggle={onCrossPostToggle} />
                )}
              </>
            )}
          </div>

          {/* Publish and Schedule Buttons */}
          {hasSelectedTargets && (
            <div className="flex items-center gap-2">
              {showSchedule ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSchedule(false)}
                    className="rounded-sm text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={isSubmitDisabled} className="rounded-sm bg-amber-500 hover:bg-amber-600">
                    Confirm Schedule
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSchedule(true)}
                    className="rounded-sm text-muted-foreground hover:text-foreground"
                  >
                    Schedule
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={isSubmitDisabled}
                    className="rounded-sm"
                    onClick={() => setShowPublishDialog(true)}
                  >
                    Publish
                  </Button>
                  <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Publish Now</DialogTitle>
                        <DialogDescription>
                          Are you sure you want to publish this post instantly?
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter className="flex gap-2 sm:gap-0">
                        <Button
                          variant="outline"
                          onClick={() => setShowPublishDialog(false)}
                        >
                          Reconsider
                        </Button>
                        <Button
                          onClick={(e) => {
                            setShowPublishDialog(false)
                            onPublishNow(e, selectedTargets)
                          }}
                        >
                          Publish
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          )}
        </div>

        <div
          className={cx(
            "mt-3 grid transition-all duration-200",
            showSchedule ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          )}
        >
          <div className="overflow-hidden">
            <div className="rounded-xl border-0 bg-background pb-2">
              <DateTimePicker value={scheduledAt} onChange={onScheduleChange} />
              {errors.scheduled_at && (
                <p className="text-sm text-destructive">{errors.scheduled_at}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-1 flex justify-center">
          <div className="w-full max-w-[860px]">
            {activePreview === "twitter" && targets.twitter && (
              <Card className="rounded-2xl shadow-sm">
                <CardContent className="p-5">
                  <TwitterThreadEditor
                    posts={twitterPosts}
                    onUpdatePost={onUpdateTwitterPost}
                    onAddPost={onAddTwitterPost}
                    onRemovePost={onRemoveTwitterPost}
                    displayName={twitterDisplayName}
                    handleLabel={twitterHandleLabel}
                    avatarUrl={twitterAvatarUrl}
                    verified={twitterVerified}
                    assets={assets}
                    onMediaChange={onMediaChange}
                    mediaSelection={mediaSelection}
                  />
                </CardContent>
              </Card>
            )}

            {activePreview === "linkedin" && targets.linkedin && (
              <Card className="rounded-2xl shadow-sm">
                <CardContent className="p-5">
                  <LinkedinThreadEditor
                    posts={linkedinPosts}
                    onUpdatePost={onUpdateLinkedinPost}
                    onAddPost={onAddLinkedinPost}
                    onRemovePost={onRemoveLinkedinPost}
                    joinedContent={linkedinPosts.join('\n\n')}
                    initialContent={linkedinPosts[0]}
                    onReplyChange={onReplyChange}
                    displayName={linkedinDisplayName}
                    handleLabel={linkedinHandleLabel}
                    avatarUrl={linkedinAvatarUrl}
                    assets={assets}
                    onMediaChange={onMediaChange}
                    mediaSelection={mediaSelection}
                    organizationId={organizationId}
                  />
                </CardContent>
              </Card>
            )}

            {!hasSelectedTargets && !twitterEnabled && !linkedinEnabled && (
              <ConnectAccountsPrompt />
            )}

            {!hasSelectedTargets && (twitterEnabled || linkedinEnabled) && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground text-center">
                  Select at least one platform to continue.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => selectPreview("twitter", twitterEnabled)}
                    disabled={!twitterEnabled}
                    className={cx(
                      "inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium transition",
                      twitterEnabled
                        ? "text-foreground hover:bg-muted/60"
                        : "cursor-not-allowed text-muted-foreground opacity-50"
                    )}
                  >
                    <XIcon className="h-3 w-3 fill-current" />
                    {twitterDisplayName}
                  </button>
                  <button
                    type="button"
                    onClick={() => selectPreview("linkedin", linkedinEnabled)}
                    disabled={!linkedinEnabled}
                    className={cx(
                      "inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium transition",
                      linkedinEnabled
                        ? "text-foreground hover:bg-muted/60"
                        : "cursor-not-allowed text-muted-foreground opacity-50"
                    )}
                  >
                    <LinkedinIcon className="h-3.5 w-3.5 fill-current" />
                    {linkedinDisplayName}
                  </button>
                </div>
              </div>
            )}

            {errors.content && (
              <p className="mt-4 text-sm text-destructive text-center">{errors.content}</p>
            )}
          </div>
        </div>
      </div>
    </form>
  )
}
